import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { devicesActions } from '@/store';
import api from '@/lib/api';
import { X, MapPin, User, Terminal } from 'lucide-react';
import DeviceForm from '@/features/settings/DeviceForm';
import LinkPanel from '@/components/shared/LinkPanel';
import VehicleTrackersPanel from '@/components/shared/VehicleTrackersPanel';
import { showToast } from '@/components/shared/Toast';
import DeviceDrawerTerminalTab from '@/features/vehicles/DeviceDrawerTerminalTab';
import useFeatures from '@/hooks/useFeatures';
import ParentLinkTab from '@/features/bus-link/ParentLinkTab';
import MaintenancePanel from '@/features/maintenance/MaintenancePanel';
import ColdChainCard from '@/features/cold-chain/ColdChainCard';
import DeviceColdPointsEditor from '@/features/cold-chain/DeviceColdPointsEditor';
import useCanManageColdChain from '@/features/cold-chain/useCanManageColdChain';

export default function DeviceDrawer({ device, open, onClose, onSaved, initialTab = 'details' }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.session.user);
  const isAdmin = user?.administrator;
  const features = useFeatures();
  const orgRole = useSelector((state) => state.org?.role);
  const canManageLinks = features.schoolBus && (isAdmin || user?.is_sultan || orgRole === 'admin' || orgRole === 'manager');

  const canManageColdChain = useCanManageColdChain() && features.temperature;

  const [tab, setTab] = useState(initialTab);
  useEffect(() => { setTab(initialTab); }, [open, initialTab]);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  if (!open) return null;

  const handleSave = async (data) => {
    try {
      let saved;
      if (data.id) {
        saved = await api.updateDevice(data.id, data);
        dispatch(devicesActions.update(saved));
      } else {
        saved = await api.createDevice(data);
        dispatch(devicesActions.update(saved));
      }
      showToast(t('settings.deviceSaved'));
      onSaved?.();
      onClose();
      return saved;
    } catch (err) {
      showToast(t('common.failedSave') + ': ' + err.message);
      throw err;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" onClick={onClose}>
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/30 backdrop-blur-[2px]" />

      {/* Drawer panel — width grows with viewport so the multi-column
          sensor form gets room to breathe. Mobile (< 768px) keeps the
          old 520px cap; above that the drawer takes 50vw, floored at
          520px and capped at 880px so it never gets narrower than today
          nor awkwardly massive on 4K monitors. */}
      <div
        className="relative w-full shadow-2xl overflow-y-auto animate-slide-in border-s border-white/20 dark:border-slate-700/50 dark:[background:rgba(15,23,42,0.88)]"
        style={{
          width: 'min(880px, max(520px, 50vw))',
          maxWidth: '100vw',
          background: 'rgba(255,255,255,0.88)',
          backdropFilter: 'blur(40px)',
          WebkitBackdropFilter: 'blur(40px)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 border-b border-white/20 dark:border-slate-700/50 px-5 py-3 flex items-center justify-between dark:[background:rgba(15,23,42,0.95)]" style={{ background: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(40px)', WebkitBackdropFilter: 'blur(40px)' }}>
          <h3 className="text-sm font-bold text-slate-800 dark:text-slate-200">
            {device?.id ? t('settings.editDevice') : t('settings.newDevice')}
          </h3>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all"
          >
            <X size={16} />
          </button>
        </div>

        {/* Tab bar */}
        {device?.id && (
          <div className="sticky top-[52px] z-10 px-5 border-b border-white/20 dark:border-slate-700/50 dark:[background:rgba(15,23,42,0.95)] flex gap-4" style={{ background: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(40px)', WebkitBackdropFilter: 'blur(40px)' }}>
            <TabButton active={tab === 'details'} onClick={() => setTab('details')}>{t('settings.details', 'Details')}</TabButton>
            {(isAdmin || user?.is_sultan) && (
              <TabButton active={tab === 'terminal'} onClick={() => setTab('terminal')}>{t('terminal.title', 'Terminal')}</TabButton>
            )}
            {canManageLinks && (
              <TabButton active={tab === 'parentLink'} onClick={() => setTab('parentLink')}>{t('parentLink.tab')}</TabButton>
            )}
            {canManageColdChain && (
              <TabButton active={tab === 'coldPoints'} onClick={() => setTab('coldPoints')}>{t('coldChain.drawerTab', 'Cold points')}</TabButton>
            )}
          </div>
        )}

        {/* Content */}
        <div className="p-5 space-y-4">
          {tab === 'details' && (
            <DeviceForm
              device={device}
              onSave={handleSave}
              onCancel={onClose}
              isAdmin={isAdmin}
              userId={user?.id}
              compact
            />
          )}
          {tab === 'terminal' && device?.id && (
            <DeviceDrawerTerminalTab deviceId={device.id} deviceName={device.name} />
          )}
          {tab === 'parentLink' && canManageLinks && device?.id && (
            <ParentLinkTab key={device.id} deviceId={device.id} deviceName={device.name} deviceAttributes={device.attributes} />
          )}

          {tab === 'coldPoints' && canManageColdChain && device?.id && (
            <>
              <ColdChainCard deviceId={device.id} />
              <DeviceColdPointsEditor key={device.id} deviceId={device.id} />
            </>
          )}

          {/* Connections — only for existing devices */}
          {tab === 'details' && device?.id && (
            <div className="space-y-2 pt-2">
              <h4 className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                {t('settings.connections')}
              </h4>
              <VehicleTrackersPanel device={device} />
              <LinkPanel
                parentType="deviceId"
                parentId={device.id}
                linkedType="geofenceId"
                fetchAll={() => api.getGeofences()}
                fetchLinked={() => api.getGeofencesByDevice(device.id)}
                labelKey="name"
                title={t('settings.geofences')}
                icon={MapPin}
                searchPlaceholder={t('settings.searchGeofences')}
              />
              <LinkPanel
                parentType="deviceId"
                parentId={device.id}
                linkedType="driverId"
                fetchAll={() => api.getDrivers()}
                fetchLinked={() => api.getDriversByDevice(device.id)}
                labelKey="name"
                title={t('settings.drivers')}
                icon={User}
                searchPlaceholder={t('settings.searchDrivers')}
              />
              <LinkPanel
                parentType="deviceId"
                parentId={device.id}
                linkedType="commandId"
                fetchAll={() => api.getAllCommands()}
                fetchLinked={() => api.getCommandsByDevice(device.id)}
                labelKey="description"
                title={t('settings.savedCommands')}
                icon={Terminal}
                searchPlaceholder={t('settings.searchCommands')}
              />
              {features.maintenance && (
                <MaintenancePanel deviceId={device.id} canEdit={isAdmin || user?.is_sultan || orgRole === 'admin' || orgRole === 'manager'} />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function TabButton({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-1 py-2.5 text-xs font-semibold transition-colors border-b-2 -mb-px ${active ? 'text-slate-900 dark:text-slate-100 border-slate-900 dark:border-slate-100' : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 border-transparent'}`}
    >
      {children}
    </button>
  );
}
